import { collection, getDocs, query, where } from 'firebase/firestore';
import type { Firestore } from 'firebase/firestore';

export type GlobalDividendCalendarEvent = {
    eventId: string;
    symbol: string;
    providerSymbol?: string;
    companyName?: string;
    exchange?: string;
    exDate?: string | null;
    paymentDate?: string | null;
    recordDate?: string | null;
    declarationDate?: string | null;
    amountPerShare?: number | null;
    currency?: string | null;
    frequency?: string | null;
    source?: 'FMP_CALENDAR' | string;
    provider?: 'FMP' | string;
    declared?: boolean;
    fetchedAt?: string;
    updatedAt?: unknown;
};

type LoadGlobalDividendEventsForSymbolsParams = {
    db: Firestore;
    symbols: string[];
    horizonDays: number;
    now?: Date | string | number;
};

const GLOBAL_DIVIDEND_CALENDAR_COLLECTION = 'dividend_calendar_global';
const FIRESTORE_IN_QUERY_LIMIT = 10;
const LOOKBACK_DAYS = 5;

const isDevEnvironment = () => (
    typeof process !== 'undefined' && process.env.NODE_ENV === 'development'
);

const toIsoDay = (value: Date) => value.toISOString().slice(0, 10);

const addDays = (date: Date, days: number) => {
    const next = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
    next.setUTCDate(next.getUTCDate() + days);
    return next;
};

const normalizeNow = (now: Date | string | number | undefined) => {
    if (now instanceof Date) return now;
    if (typeof now === 'string' || typeof now === 'number') return new Date(now);
    return new Date();
};

const normalizeSymbols = (symbols: string[]) => (
    Array.from(new Set(
        symbols
            .map((symbol) => String(symbol || '').trim().toUpperCase())
            .filter(Boolean)
    ))
);

const chunkSymbols = (symbols: string[]) => {
    const chunks: string[][] = [];
    for (let index = 0; index < symbols.length; index += FIRESTORE_IN_QUERY_LIMIT) {
        chunks.push(symbols.slice(index, index + FIRESTORE_IN_QUERY_LIMIT));
    }
    return chunks;
};

const normalizeDateString = (value: unknown) => {
    if (typeof value !== 'string') return null;
    const trimmed = value.trim();
    if (!trimmed) return null;
    return trimmed.slice(0, 10);
};

const effectiveDateForEvent = (event: Pick<GlobalDividendCalendarEvent, 'paymentDate' | 'exDate'>) => (
    normalizeDateString(event.paymentDate) || normalizeDateString(event.exDate)
);

const toNullableString = (value: unknown) => (typeof value === 'string' ? value : value == null ? null : undefined);
const toOptionalString = (value: unknown) => (typeof value === 'string' ? value : undefined);
const toNullableNumber = (value: unknown) => (typeof value === 'number' && Number.isFinite(value) ? value : value == null ? null : undefined);

const toGlobalDividendCalendarEvent = (doc: { id: string; data: () => Record<string, unknown> }): GlobalDividendCalendarEvent => {
    const data = doc.data() || {};
    return {
        eventId: toOptionalString(data.eventId) || doc.id,
        symbol: toOptionalString(data.symbol) || '',
        providerSymbol: toOptionalString(data.providerSymbol),
        companyName: toOptionalString(data.companyName),
        exchange: toOptionalString(data.exchange),
        exDate: toNullableString(data.exDate),
        paymentDate: toNullableString(data.paymentDate),
        recordDate: toNullableString(data.recordDate),
        declarationDate: toNullableString(data.declarationDate),
        amountPerShare: toNullableNumber(data.amountPerShare),
        currency: toNullableString(data.currency),
        frequency: toNullableString(data.frequency),
        source: toOptionalString(data.source),
        provider: toOptionalString(data.provider),
        declared: typeof data.declared === 'boolean' ? data.declared : undefined,
        fetchedAt: toOptionalString(data.fetchedAt),
        updatedAt: data.updatedAt,
    };
};

export async function loadGlobalDividendEventsForSymbols({
    db,
    symbols,
    horizonDays,
    now,
}: LoadGlobalDividendEventsForSymbolsParams): Promise<GlobalDividendCalendarEvent[]> {
    const requestedSymbols = symbols;
    const normalizedSymbols = normalizeSymbols(symbols);

    if (normalizedSymbols.length === 0) {
        if (isDevEnvironment()) {
            console.debug('[dividends-global-read] diagnostics', {
                requestedSymbols,
                normalizedSymbols,
                queryChunks: [],
                rawDocs: 0,
                returnedEvents: 0,
                horizonDays,
            });
        }
        return [];
    }

    const today = normalizeNow(now);
    const from = toIsoDay(addDays(today, -LOOKBACK_DAYS));
    const to = toIsoDay(addDays(today, horizonDays));
    const symbolSet = new Set(normalizedSymbols);
    const chunks = chunkSymbols(normalizedSymbols);
    const eventsById = new Map<string, GlobalDividendCalendarEvent>();
    let rawDocs = 0;
    let filteredOutMissingDate = 0;
    let filteredOutOutOfRange = 0;
    let filteredOutSymbolMismatch = 0;
    const rawGOOGLDocs: GlobalDividendCalendarEvent[] = [];
    const returnedGOOGLEvents: GlobalDividendCalendarEvent[] = [];

    await Promise.all(chunks.map(async (symbolChunk) => {
        const snapshot = await getDocs(query(
            collection(db, GLOBAL_DIVIDEND_CALENDAR_COLLECTION),
            where('symbol', 'in', symbolChunk)
        ));

        for (const doc of snapshot.docs) {
            rawDocs += 1;
            const event = toGlobalDividendCalendarEvent(doc);
            const normalizedEventSymbol = event.symbol.trim().toUpperCase();
            const effectiveDate = effectiveDateForEvent(event);

            if (normalizedEventSymbol === 'GOOGL') rawGOOGLDocs.push(event);
            if (!event.eventId || !symbolSet.has(normalizedEventSymbol)) { filteredOutSymbolMismatch += 1; continue; }
            if (!effectiveDate) { filteredOutMissingDate += 1; continue; }
            if (effectiveDate < from || effectiveDate > to) { filteredOutOutOfRange += 1; continue; }

            eventsById.set(event.eventId, {
                ...event,
                symbol: normalizedEventSymbol,
            });
        }
    }));

    const events = Array.from(eventsById.values()).sort((left, right) => {
        const leftDate = effectiveDateForEvent(left) || '';
        const rightDate = effectiveDateForEvent(right) || '';
        if (leftDate !== rightDate) return leftDate.localeCompare(rightDate);
        return left.symbol.localeCompare(right.symbol);
    });

    events.forEach((event) => { if (event.symbol === 'GOOGL') returnedGOOGLEvents.push(event); });

    if (isDevEnvironment()) {
        console.debug('[dividends-global-read] diagnostics', {
            requestedSymbols,
            normalizedSymbols,
            queryChunks: chunks,
            rawDocs,
            returnedEvents: events.length,
            filteredOutMissingDate,
            filteredOutOutOfRange,
            filteredOutSymbolMismatch,
            sampleReturnedEvents: events.slice(0, 10).map((event) => ({ symbol: event.symbol, exDate: event.exDate, paymentDate: event.paymentDate, amountPerShare: event.amountPerShare, currency: event.currency })),
            requestedGOOGL: normalizedSymbols.includes('GOOGL'),
            rawGOOGLDocs,
            returnedGOOGLEvents,
            horizonDays,
        });
    }

    return events;
}
